import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Panel, Button, Callout, Icon, Skeleton } from '../../../../components';
import { FtuxTarget } from '../../../../features/ftux/FtuxTarget';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { ANIM, CASINO_SETTINGS, COLORS } from '../../../../theme/tokens';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '../../../../theme/customizer-tokens';
import { useThemeMode } from '../../../../theme/OwsTheme';
import { DotField } from '../../../customizer/components/DotField';
import type { LogoEditWindow } from '../../../../mock-data/types';
import type { DisplayLogo, LogoOrigin } from '../../logos';
import { FAVICON_SLOT } from '../../logo-api';
import { useAutoFrame } from '../../useAutoFrame';
import { SectionSubtitle } from '../shared.styles';
import { DownloadGlyph, ImagePlaceholderGlyph, LogoMarkGlyph } from '../Glyphs/Glyphs';
import {
  LogosHead,
  LogosTitle,
  Banner,
  BannerIcon,
  BannerText,
  PrimaryRow,
  PrimaryBody,
  PrimaryFrameWrap,
  Frame,
  ArtStack,
  PreviewImg,
  PrimaryMeta,
  PrimaryHead,
  PrimaryLabel,
  PrimaryNote,
  Actions,
  LockNote,
  VariantsHead,
  VariantsLabel,
  Grid,
  Card,
  CardMeta,
  CardHead,
  CardLabel,
  InfoBtn,
  InfoPop,
  InfoPopText,
  CardActions,
  TileBtn,
  OriginPill,
  OriginMark,
  OriginText,
  SkelStack,
  SkelGrid,
  PlaceholderWrap,
  PlaceholderBadge,
  PlaceholderTitle,
  PlaceholderText,
  PlaceholderGrid,
  GhostFrame,
  GhostGlyph,
  GhostLabel,
  FieldOverlay,
} from './LogosSection.styles';

/* The multicolour Lumio mark, same asset as the Lumio panel header and the top bar. A
 * gradient asset, so it renders as a plain <img>, not an Icon glyph. */
const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';

/* Where the FTUX's logo piece lights the primary logo, its Replace logo marked inside it (inert outside an FTUX step). */
const PRIMARY_LOGO_TARGET: FtuxTargetId = 'logo-primary';

export type LogosSectionProps = {
  primary: DisplayLogo;
  variants: DisplayLogo[];
  window: LogoEditWindow;
  loading: boolean;
  /**
   * The casino's real logos exist. While false, the section shows an honest placeholder
   * instead of the schematic fallback art (the fallback reads as a finished logo, which is
   * misleading before logos are generated).
   *
   * Deliberately NOT also gated on the deploy logo step having finished: artwork that is
   * mid-rewrite is still the artwork the site is serving, and hiding it behind the empty
   * placeholder loses the owner's entire logo set for the length of a run. A run in flight
   * belongs in `updating`, which draws OVER the logos rather than replacing them.
   */
  ready: boolean;
  /** No logos yet AND the deploy logo step is queued/running — tunes the placeholder copy
   *  from "not available" to "on the way". */
  generating: boolean;
  /**
   * The read of the logos failed. With no logos `ready`, the section says so and offers `onRetry` in place of the
   * placeholder: a failed read is not a brand without logos.
   */
  failed?: boolean;
  /** Reads the logos again (the failed read's retry). */
  onRetry?: () => void;
  /**
   * Slot ids currently being rewritten. Usually more than the one the owner picked: a
   * rebuild redraws everything derived from it, and all of them change together. May be the
   * whole set, when all the page can see is that this brand's logo step is running.
   */
  updating: string[];
  /** Replace ONE logo (the primary or any variant) with the owner's own artwork. */
  onReplace: (logo: DisplayLogo) => void;
  /** Download a single logo (the page owns the fetch + cross-origin fallback). */
  onDownload: (logo: DisplayLogo) => void;
  onDownloadAll: () => void;
};

/**
 * Reactively translate a logo-variant label. The labels are a fixed UI vocabulary that
 * originates as English keys in `../../logos` (the pipeline slot map) and the brand's mock
 * variant set; we localise them here at the render site so the catalog harvests each literal
 * while the source constants stay English. An unknown / generically-humanised slot label
 * passes through untouched (it's derived from the slot id, not a fixed phrase).
 */
export const localizeLogoLabel = (label: string, t: TFunction): string => {
  const labels: Record<string, string> = {
    // Pipeline slot labels (../../logos SLOT_LABELS).
    'Primary logo': t('Primary logo'),
    'Symbol + name': t('Symbol + name'),
    'Symbol + short name': t('Symbol + short name'),
    Wordmark: t('Wordmark'),
    'Wordmark · black': t('Wordmark · black'),
    'Wordmark · white': t('Wordmark · white'),
    'Short name': t('Short name'),
    Symbol: t('Symbol'),
    // Also a schematic fallback label (mock-data casino-settings).
    Favicon: t('Favicon'),
    // Schematic fallback variant labels (mock-data casino-settings).
    'Horizontal · dark': t('Horizontal · dark'),
    Stacked: t('Stacked'),
    Monochrome: t('Monochrome'),
  };
  return labels[label] ?? label;
};

/**
 * WHERE each logo is used, which is what an owner actually needs to know before replacing
 * one: the pipeline's own `alt` describes what the artwork looks like ("The symbol and
 * short name of the casino are displayed horizontally…"), which the preview beside it
 * already shows. This copy wins over that; a slot we don't have copy for falls back to the
 * record's description (see `DisplayLogo.description`). Localised at the render site (the
 * slot ids are data, the copy is a fixed vocabulary), exactly like `localizeLogoLabel`.
 */
export const localizeSlotDescription = (slot: string, t: TFunction): string => {
  const copy: Record<string, string> = {
    symbol_and_name_horizontal: t('Used in your website header and in the emails your players receive.'),
    name_horizontal: t('Used in your website header at some screen sizes.'),
    symbol_and_name_vertical: t('Used in your website header at some screen sizes.'),
    symbol_and_short_name_horizontal: t('Used in your website header on mobile.'),
    name_horizontal_black: t('Used in your website footer.'),
    name_horizontal_white: t('Used in your website footer.'),
    short_name: t('Used in your website header at some screen sizes.'),
    symbol: t('Used on your loading and splash screens.'),
    [FAVICON_SLOT]: t('Used as your website favicon, the small icon in the browser tab.'),
  };
  return copy[slot] ?? '';
};

/**
 * Says who made this logo: the branding pipeline, or the owner. Every logo carries one,
 * so a set that has been partly replaced is readable at a glance. Lumio's own mark leads
 * the generated pill (the same asset as the Lumio panel and the replace overlay), so it
 * is recognisably the assistant's work rather than a generic decoration.
 */
/**
 * Who made this logo — or, while a replacement is running, that it is being remade.
 *
 * `updating` deliberately outranks the origin: mid-run the origin still describes the
 * artwork ON SCREEN (the old one, which the site is still serving), and labelling it
 * "Custom" the moment the owner pressed Replace would claim their file is already live.
 */
const OriginLabel = ({ origin, updating }: { origin: LogoOrigin; updating: boolean }): React.ReactElement =>
  updating ? (
    <OriginPill $origin="generated">
      <OriginMark src={LUMIO_MARK_SRC} alt="" />
      <OriginText text="Updating…" tag="span" />
    </OriginPill>
  ) : origin === 'custom' ? (
    <OriginPill $origin="custom">
      <Icon name="edit" size={11} color={COLORS.accent} />
      <OriginText text="Custom" tag="span" />
    </OriginPill>
  ) : (
    <OriginPill $origin="generated">
      <OriginMark src={LUMIO_MARK_SRC} alt="" />
      <OriginText text="Lumio" tag="span" />
    </OriginPill>
  );

/**
 * "What is this logo for?" — the slot's brief, on demand.
 *
 * Click-opened rather than hover-opened: the brief is a sentence worth reading, hover
 * tooltips vanish the moment the pointer drifts, and a phone has no hover at all. It spans
 * the tile's full width so the grid column can never clip it.
 */
const SlotBrief = ({ label, description }: { label: string; description: string }): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    // Swallowed so Escape closes THIS first; a second press then reaches the page.
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  return (
    <CardHead ref={wrapRef}>
      <CardLabel text={label} tag="span" title={label} />
      <InfoBtn
        type="button"
        aria-label={t('What {{label}} is used for', { label })}
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        <Icon name="info" size={14} />
      </InfoBtn>
      {open ? (
        <InfoPop role="tooltip">
          <InfoPopText text={description} tag="span" />
        </InfoPop>
      ) : null}
    </CardHead>
  );
};

/**
 * Cross-fade between successive versions of one logo.
 *
 * A run rewrites the artwork behind a slot that is already on screen: the tile does not
 * change, its `src` does. Painting the new file straight into the `<img>` makes it pop in
 * mid-page, so the replacement is preloaded first and only then faded up over the copy it
 * replaces, which is dropped once the fade is done.
 *
 * Returns the settled artwork and, while one is arriving, the incoming copy plus whether
 * it has been lit yet. A src that never loads still swaps (there is nothing to wait for),
 * so a broken CDN entry can't strand the tile on artwork the site no longer serves.
 */
const useArtworkSwap = (src: string): { shown: string; entering: string | null; lit: boolean } => {
  const [shown, setShown] = React.useState(src);
  const [entering, setEntering] = React.useState<string | null>(null);
  const [lit, setLit] = React.useState(false);

  React.useEffect(() => {
    if (src === shown) {
      setEntering(null);
      setLit(false);
      return undefined;
    }
    let cancelled = false;
    let started = false;
    let raf = 0;
    const begin = (): void => {
      if (cancelled || started) return;
      started = true;
      setEntering(src);
      // Lit on the NEXT frame so the opacity animates 0→1; mounting at 1 would pop.
      raf = window.requestAnimationFrame(() => setLit(true));
    };
    const img = new Image();
    img.onload = begin;
    img.onerror = begin;
    img.src = src;
    // A warm cache decodes before the handlers are ever called, and `onload` then never
    // fires — the tile would sit on the old artwork for good.
    if (img.complete) begin();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, [src, shown]);

  // Promote once the fade is over: the incoming copy becomes the settled one and the
  // outgoing layer is dropped.
  React.useEffect(() => {
    if (!entering || !lit) return undefined;
    const timer = window.setTimeout(() => setShown(entering), ANIM.LOGO_SWAP_MS);
    return () => window.clearTimeout(timer);
  }, [entering, lit]);

  return { shown, entering, lit };
};

/**
 * A logo inside its preview frame, cross-faded across replacements.
 *
 * The outgoing copy stays soft while its successor arrives (`$dim`), so a tile that was
 * blurred behind the regeneration field resolves INTO the new artwork rather than snapping
 * back to the old one first. The frame's plate travels in the same beat (see `Frame`).
 *
 * `fill` scales artwork that is tiny by design up to the frame's height (see `PreviewImg`).
 */
const LogoArtwork = ({
  src,
  alt,
  updating,
  fill = false,
}: {
  src: string;
  alt: string;
  updating: boolean;
  fill?: boolean;
}): React.ReactElement => {
  const { shown, entering, lit } = useArtworkSwap(src);
  return (
    <ArtStack>
      <PreviewImg src={shown} alt={entering ? '' : alt} $dim={updating || entering !== null} $fill={fill} />
      {/* The incoming copy carries the label once it is on screen, so the frame is never
          described twice and never goes unlabelled mid-swap. */}
      {entering ? <PreviewImg src={entering} alt={alt} $enter $lit={lit} $fill={fill} /> : null}
    </ArtStack>
  );
};

/**
 * Mount + cross-fade lifecycle for a tile's regeneration field, mirroring the deploy
 * preview's grid reveal: the canvas mounts and fades in (0→1) when the run starts, then
 * cross-fades out as the new artwork lands and finally unmounts — which is what stops
 * its animation frame loop. `mounted` gates the canvas, `shown` drives the opacity.
 */
const useFieldReveal = (active: boolean): { mounted: boolean; shown: boolean } => {
  const [mounted, setMounted] = React.useState(active);
  const [shown, setShown] = React.useState(false);
  const unmountRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    window.clearTimeout(unmountRef.current);
    if (active) {
      setMounted(true);
      // Flip on the NEXT frame so the opacity animates 0→1 (mounting straight at 1
      // would pop in with no transition).
      const raf = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(raf);
    }
    setShown(false);
    unmountRef.current = window.setTimeout(() => setMounted(false), ANIM.LOGO_FIELD_FADE_MS);
    return undefined;
  }, [active]);

  React.useEffect(() => () => window.clearTimeout(unmountRef.current), []);

  return { mounted, shown };
};

/**
 * The "this logo is being remade" field: the drifting dot lattice from the onboarding
 * deploy step (where it runs over the website previews while the site is built), reused
 * over a single logo frame while a replacement run rewrites it. Decorative — the state
 * itself is already announced by the tile's "Updating…" label, so it is hidden from
 * assistive tech.
 *
 * The dot ink follows the frame's PLATE, not the studio theme: the plates are contrast
 * backings picked per-logo (`useAutoFrame`) and are pinned against the light/dark palette,
 * so a near-white field on the dark plate and its near-black counterpart on the light one
 * read the same either way.
 */
const UpdatingField = ({
  frame,
  seed,
  active,
}: {
  frame: 'light' | 'dark';
  /** Distinct per tile so a grid of fields never drifts in lockstep. */
  seed: number;
  active: boolean;
}): React.ReactElement | null => {
  const { mounted, shown } = useFieldReveal(active);
  if (!mounted) return null;
  return (
    <FieldOverlay $shown={shown} aria-hidden="true">
      <DotField seed={seed} fill={frame === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL} />
    </FieldOverlay>
  );
};

/** One contained logo tile — auto-tones its frame to whatever artwork it holds. */
const LogoTile = ({
  logo,
  canReplace,
  updating,
  seed,
  onReplace,
  onDownload,
}: {
  logo: DisplayLogo;
  canReplace: boolean;
  /** A replacement run is currently rewriting this logo. */
  updating: boolean;
  /** The tile's position in the grid — seeds its dot field so no two drift in lockstep. */
  seed: number;
  onReplace: (logo: DisplayLogo) => void;
  onDownload: (logo: DisplayLogo) => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const frame = useAutoFrame(logo.src, logo.frame);
  const label = localizeLogoLabel(logo.label, t);
  // Ours wins, the record's own description stands in for a slot we have no copy for —
  // the same order the replace dialog uses, so both say the same thing.
  const brief = localizeSlotDescription(logo.id, t) || logo.description?.trim() || '';
  return (
    <Card>
      {/* Name first, then the artwork, then the controls: the tile reads top-down as
          "what this logo is → what it looks like → what you can do with it". */}
      {brief ? <SlotBrief label={label} description={brief} /> : <CardLabel text={label} tag="span" title={label} />}
      <Frame $frame={frame}>
        {/* The favicon is stored at 64 px: drawn at its own size it read as a smaller logo than its neighbours. */}
        <LogoArtwork
          src={logo.src}
          alt={t('{{label}} logo', { label })}
          updating={updating}
          fill={logo.id === FAVICON_SLOT}
        />
        <UpdatingField frame={frame} seed={seed} active={updating} />
      </Frame>
      <CardMeta>
        <CardActions>
          <TileBtn
            type="button"
            aria-label={t('Replace {{label}}', { label })}
            title={
              updating
                ? t('This logo is being updated')
                : canReplace
                  ? t('Replace {{label}}', { label })
                  : t('Your logos are locked')
            }
            disabled={!canReplace || updating}
            onClick={() => {
              if (!canReplace || updating) return;
              onReplace(logo);
            }}
          >
            <Icon name="edit" size={15} />
          </TileBtn>
          <TileBtn type="button" aria-label={t('Download {{label}}', { label })} onClick={() => onDownload(logo)}>
            <DownloadGlyph size={16} />
          </TileBtn>
        </CardActions>
        <OriginLabel origin={logo.origin} updating={updating} />
      </CardMeta>
    </Card>
  );
};

/**
 * Honest stand-in shown until the casino's real logos have been generated. Clearly
 * reads as "your logos will appear here" (a dashed neutral mark + copy, then ghost
 * variant tiles), so the schematic fallback never masquerades as a finished brand.
 * Mirrors the real variants grid, so the layout doesn't jump when logos reveal.
 *
 * While the first set is actually being drawn, each ghost well runs the same field the
 * tiles use for a replacement, so a casino's very first generation is as alive as every
 * later one. Only then: with no run in flight there is nothing to animate, and a lattice
 * drifting under "no logo set available yet" would claim work that isn't happening.
 */
const LogosPlaceholder = ({
  generating,
  variants,
}: {
  generating: boolean;
  variants: DisplayLogo[];
}): React.ReactElement => {
  const { t } = useTranslation();
  // These wells are the STUDIO surface (`surfaceFaint`), not one of the pinned artwork
  // plates, so unlike a logo tile their tone follows the app theme and the ink has to be
  // resolved rather than derived from the artwork. Light studio → dark dots, and back.
  const plate = useThemeMode();
  return (
    <PlaceholderWrap>
      <PlaceholderBadge $pulse={generating}>
        <LogoMarkGlyph size={30} color={COLORS.textFaint} />
      </PlaceholderBadge>
      {generating ? (
        <PlaceholderTitle text="Your logos are being generated" tag="span" />
      ) : (
        <PlaceholderTitle text="No logo set available yet" tag="span" />
      )}
      {generating ? (
        <PlaceholderText
          text="Your primary logo and every variant will appear here once the Logos step of your website's deployment finishes."
          tag="span"
        />
      ) : (
        <PlaceholderText
          text="Your primary logo and its variants will appear here once they have been generated."
          tag="span"
        />
      )}
      {variants.length > 0 ? (
        <PlaceholderGrid>
          {variants.map((variant, index) => (
            <Card key={variant.id}>
              {/* Name above the well, mirroring the real tile, so nothing shifts on reveal. */}
              <GhostLabel text={localizeLogoLabel(variant.label, t)} tag="span" />
              <GhostFrame>
                {/* Seeded from 1, matching the real grid, so no two wells drift in lockstep. */}
                <UpdatingField frame={plate} seed={index + 1} active={generating} />
                <GhostGlyph>
                  <ImagePlaceholderGlyph size={22} />
                </GhostGlyph>
              </GhostFrame>
            </Card>
          ))}
        </PlaceholderGrid>
      ) : null}
    </PlaceholderWrap>
  );
};

/**
 * Website Logos — the brand's main logo plus every variant of it, each contained in a
 * uniform preview frame (the logo's own size never dictates the tile, and the frame
 * auto-tones for contrast). Every logo can be downloaded, carries a label saying whether
 * it is still the generated artwork or the owner's own, and can be replaced individually
 * while the edit window is open (the green banner) — once the registered-player cap has
 * closed it (the gold banner) every Replace action is disabled. Reveal is gated on
 * `loading`; until the logos are `ready` (real assets present + the deploy logo step
 * done) a placeholder stands in for the misleading schematic fallback.
 *
 * A logo the pipeline is currently rewriting (`updating`) softens behind the same drifting
 * dot field the onboarding deploy step runs over the website previews, so the wait for new
 * artwork is visibly alive rather than a static tile with a label on it.
 */
export const LogosSection = ({
  primary,
  variants,
  window: editWindow,
  loading,
  ready,
  generating,
  failed = false,
  onRetry,
  updating,
  onReplace,
  onDownload,
  onDownloadAll,
}: LogosSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const primaryFrame = useAutoFrame(primary.src, primary.frame);
  const primaryUpdating = updating.includes(primary.id);

  return (
    <Panel>
      <LogosHead>
        <LogoMarkGlyph size={22} color={COLORS.iconBright} />
        <LogosTitle text="Website Logos" tag="span" />
      </LogosHead>
      <SectionSubtitle
        text="Your main logo and each of its variants. Download any of them, or replace one with your own artwork."
        tag="span"
      />

      {loading ? (
        <SkelStack>
          <Skeleton block height={56} radius={12} />
          <Skeleton block height={150} radius={12} />
          <SkelGrid>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <Skeleton key={index} block height={156} radius={12} />
            ))}
          </SkelGrid>
        </SkelStack>
      ) : !ready && failed ? (
        <Callout
          tone="danger"
          title="We couldn't load this page"
          text="Please try again."
          action={onRetry ? <Button variant="outline" icon="refresh" label={t('Try again')} onClick={onRetry} /> : null}
        />
      ) : !ready ? (
        <LogosPlaceholder generating={generating} variants={variants} />
      ) : (
        <>
          {!editWindow.countKnown ? (
            /* The count could not be read. Replacing stays shut (an under-count would open an
               edit the brand's real size forbids), but the count-based copy is withheld rather
               than stating a number nobody measured. */
            <Banner $tone="lock">
              <BannerIcon>
                <Icon name="warning" size={16} color={CASINO_SETTINGS.lockFg} />
              </BannerIcon>
              <BannerText
                text="We can't check your player count right now, so replacing logos is unavailable for the moment. Everything else here still works."
                tag="span"
              />
            </Banner>
          ) : editWindow.editable ? (
            <Banner $tone="ok">
              <BannerIcon>
                <Icon name="check" size={16} color={CASINO_SETTINGS.okFg} />
              </BannerIcon>
              <BannerText
                text="You can still replace your logos. To protect your brand and your players, changes stay open until {{maxUsers}} players have registered ({{registeredUsers}} so far)."
                placeholders={{ maxUsers: editWindow.maxUsers, registeredUsers: editWindow.registeredUsers }}
                tag="span"
              />
            </Banner>
          ) : (
            <Banner $tone="lock">
              <BannerIcon>
                <Icon name="warning" size={16} color={CASINO_SETTINGS.lockFg} />
              </BannerIcon>
              <BannerText
                text="Your logos are locked. To protect your brand and your players, logos can't be changed once {{maxUsers}} players have registered ({{registeredUsers}} so far)."
                placeholders={{ maxUsers: editWindow.maxUsers, registeredUsers: editWindow.registeredUsers }}
                tag="span"
              />
            </Banner>
          )}

          <PrimaryRow>
            <PrimaryBody data-ftux={PRIMARY_LOGO_TARGET}>
              <PrimaryFrameWrap>
                <Frame $frame={primaryFrame} $tall>
                  <LogoArtwork
                    src={primary.src}
                    alt={t('{{label}} logo', { label: localizeLogoLabel(primary.label, t) })}
                    updating={primaryUpdating}
                  />
                  {/* Seed 0 — the variants below continue from 1, so no two fields drift alike. */}
                  <UpdatingField frame={primaryFrame} seed={0} active={primaryUpdating} />
                </Frame>
              </PrimaryFrameWrap>
              <PrimaryMeta>
                <PrimaryHead>
                  <PrimaryLabel text="Primary logo" tag="span" />
                  <OriginLabel origin={primary.origin} updating={primaryUpdating} />
                </PrimaryHead>
                <PrimaryNote text="Your main logo, used across your website and emails." tag="span" />
                <Actions>
                  <FtuxTarget id="logo-replace">
                    <Button
                      label={primaryUpdating ? t('Updating…') : t('Replace logo')}
                      icon="edit"
                      variant="primary"
                      disabled={!editWindow.editable || primaryUpdating}
                      onClick={() => {
                        if (!editWindow.editable || primaryUpdating) return;
                        onReplace(primary);
                      }}
                    />
                  </FtuxTarget>
                  <Button label={t('Download')} variant="outline" onClick={() => onDownload(primary)} />
                </Actions>
                {!editWindow.editable ? (
                  <LockNote text="Your logos are locked and can no longer be replaced." tag="span" />
                ) : null}
              </PrimaryMeta>
            </PrimaryBody>
          </PrimaryRow>

          {variants.length > 0 ? (
            <>
              <VariantsHead>
                <VariantsLabel text="Logo variants" tag="span" />
                <Button label={t('Download all')} variant="outline" onClick={onDownloadAll} />
              </VariantsHead>
              <Grid>
                {variants.map((variant, index) => (
                  <LogoTile
                    key={variant.id}
                    logo={variant}
                    canReplace={editWindow.editable}
                    updating={updating.includes(variant.id)}
                    seed={index + 1}
                    onReplace={onReplace}
                    onDownload={onDownload}
                  />
                ))}
              </Grid>
            </>
          ) : null}
        </>
      )}
    </Panel>
  );
};

export default LogosSection;
